import { ChevronLeft } from "lucide-react";
import { Label } from "@/components/ui/label";
import { InputWithLabel } from "@/pages/components/input-label";
import { Button } from "@/components/ui/button";
import BackNavigator from "@/pages/components/back-navigation";
import { useForm } from "@tanstack/react-form";
import { locatePageSchema } from "@/lib/form-schema";
import { cn, describeMissingFields } from "@/lib/utils";
import InputDate from "@/pages/components/input-date";
import type { ILocate, ILocateResponse } from "@/hooks/use-locate";
import useLocate from "@/hooks/use-locate";
import { useNavigate } from "react-router-dom";
import { formatDateToMMDDYYYY } from "@/lib/date-utils";

const Locate = () => {
  const navigate = useNavigate();
  const locateQuery = useLocate();
  const form = useForm({
    defaultValues: {
      firstName: "",
      lastName: "",
      zipCode: "",
      dob: undefined,
    },
    onSubmit: ({ value }) => {
      const userInfo: ILocate = {
        mbrFirstName: value.firstName,
        mbrLastName: value.lastName,
        mbrZip: value.zipCode,
        mbrDob: formatDateToMMDDYYYY(value.dob) || "",
        mbrInputEmail: "",
      };
      locateQuery.mutate(userInfo, {
        onSuccess: (data: ILocateResponse) => {
          if (!data.success) return;
          navigate(-1);
        },
      });
    },
  });

  return (
    <main className="flex h-full flex-col items-center justify-center gap-8 px-10">
      <BackNavigator
        title="Locate Account"
        left={
          <Button
            variant="ghost"
            aria-label="Go back"
            className="left-0 h-auto w-fit bg-transparent p-0 hover:bg-transparent hover:shadow-none focus:bg-transparent focus:shadow-none active:bg-transparent"
            onClick={() => {
              if (window.history.length > 1) {
                navigate(-1);
              } else {
                navigate("/nav-error");
              }
            }}
          >
            <ChevronLeft size={20} />
          </Button>
        }
      />
      <div className="mt-16 h-12 max-w-32">
        <img
          src={
            (window as Window & { __PRODUCT_CONFIG__: { icon: string } })
              .__PRODUCT_CONFIG__.icon
          }
          alt="logo"
          className="h-full object-contain"
        />
      </div>
      <Label>Please enter your personal details to locate your account</Label>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          form.handleSubmit();
        }}
        className="mb-5 flex flex-col items-center gap-6"
      >
        <section>
          <div className="flex min-w-[300px] flex-col gap-3">
            {/* First Name */}
            <section className="">
              <form.Field
                name="firstName"
                validators={{
                  onBlur: ({ value }) => {
                    const result =
                      locatePageSchema.shape.firstName.safeParse(value);
                    return result.success
                      ? undefined
                      : result.error.errors[0].message;
                  },
                }}
              >
                {(field) => (
                  <div className="flex flex-row items-center space-x-2">
                    <InputWithLabel
                      id="firstName"
                      label="First Name"
                      placeholder="First Name"
                      value={field.state.value}
                      onChange={(newValue) => field.handleChange(newValue)}
                      onBlur={() => field.handleBlur()}
                      error={field.state.meta.errors[0]}
                    />
                  </div>
                )}
              </form.Field>
            </section>
            {/* Last Name */}
            <section className="">
              <form.Field
                name="lastName"
                validators={{
                  onBlur: ({ value }) => {
                    const result =
                      locatePageSchema.shape.lastName.safeParse(value);
                    return result.success
                      ? undefined
                      : result.error.errors[0].message;
                  },
                }}
              >
                {(field) => (
                  <div className="flex flex-row items-center space-x-2">
                    <InputWithLabel
                      id="lastName"
                      label="Last Name"
                      placeholder="Last Name"
                      onChange={(newValue) => field.handleChange(newValue)}
                      onBlur={() => field.handleBlur()}
                      error={field.state.meta.errors[0]}
                    />
                  </div>
                )}
              </form.Field>
            </section>

            {/* Zip Code */}
            <section className="">
              <form.Field
                name="zipCode"
                validators={{
                  onBlur: ({ value }) => {
                    const result =
                      locatePageSchema.shape.zipCode.safeParse(value);
                    return result.success
                      ? undefined
                      : result.error.errors[0].message;
                  },
                }}
              >
                {(field) => (
                  <div className="flex flex-row items-center space-x-2">
                    <InputWithLabel
                      id="zipCode"
                      label="Zip Code"
                      placeholder="Zip Code"
                      onChange={(newValue) => field.handleChange(newValue)}
                      onBlur={() => field.handleBlur()}
                      error={field.state.meta.errors[0]}
                    />
                  </div>
                )}
              </form.Field>
            </section>

            {/* dob */}
            <section className="">
              <form.Field
                name="dob"
                validators={{
                  onChange: ({ value }) => {
                    const result = locatePageSchema.shape.dob.safeParse(value);
                    return result.success
                      ? undefined
                      : result.error.errors[0].message;
                  },
                }}
              >
                {(field) => (
                  <InputDate
                    id="dob"
                    label="Date of Birth"
                    placeholder="DOB"
                    value={field.state.value}
                    onChange={(newDate) => field.handleChange(newDate)}
                    error={field.state.meta.errors[0]}
                  />
                )}
              </form.Field>
            </section>
          </div>
        </section>
        <section>
          <form.Subscribe
            selector={(state) => [
              state.canSubmit,
              state.values.firstName,
              state.values.lastName,
              state.values.zipCode,
              state.values.dob,
            ]}
          >
            {([canSubmit, firstName, lastName, zipCode, dob]) => {
              // 3.3.1 Error Identification — A: names the specific empty
              // field(s) rather than a static list.
              const requirement =
                describeMissingFields([
                  { label: "first name", value: firstName },
                  { label: "last name", value: lastName },
                  { label: "zip code", value: zipCode },
                  { label: "date of birth", value: dob },
                ]) || "Disabled. Fix the errors above to continue.";
              return (
                <>
                  <Button
                    type="submit"
                    size="lg"
                    variant="default"
                    className={cn(
                      "my-2 w-[120px] bg-primary text-sm hover:bg-primary-selected sm:px-2",
                      !canSubmit && "opacity-50",
                    )}
                    disabled={locateQuery.isPending}
                    aria-disabled={!canSubmit}
                    aria-describedby={
                      !canSubmit ? "locate-requirement" : undefined
                    }
                  >
                    {locateQuery.isPending ? "Locating..." : "Locate"}
                  </Button>
                  {!canSubmit && (
                    <p id="locate-requirement" className="sr-only">
                      {requirement}
                    </p>
                  )}
                </>
              );
            }}
          </form.Subscribe>
        </section>
      </form>
    </main>
  );
};

export default Locate;
